import { useCallback } from "react";
import { QueryClient, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useOptimizedQueryKey } from "@/hooks/useOptimizedQueryKey";
import { useAuthReady } from "@/hooks/useAuthReady";
import { fetchFormTemplates } from "@/hooks/useFormTemplatesQuery";
import { fetchFormInvitations } from "@/hooks/useFormInvitationsQuery";
import { QUERY_STALE_TIME, QUERY_GC_TIME } from "@/lib/queryConfig";
import { orgQueryKeys } from "@/lib/queryKeys";
import { logIgnoredOrgRoutePrefetch } from "@/lib/logging";
import { ensureAuthenticatedSession } from "@/lib/authRetry";
import { ensureMembershipForCurrentUser, getErrorCode, getErrorMessage } from "@/lib/supabase/rpc-helpers";
import type { OrgFormResponse } from "@/types/rpc";
import { logger } from '@/lib/logger';

const DEFAULT_PAGE = 1;
const DEFAULT_PAGE_SIZE = 50;

const DEFAULT_SUBMISSION_FILTERS = {
  search: "",
  status: [] as string[],
  formTemplate: "all",
  sortBy: "created_at",
  sortOrder: "desc",
  showPending: true,
  showReviewed: true,
  applicantName: "",
};

const DEFAULT_SUBMISSION_FILTERS_KEY = JSON.stringify(DEFAULT_SUBMISSION_FILTERS);

const RENEWAL_REQUESTS_QUERY_SCOPE = ["invitation", "renewal", "count"];

interface FormResponseRow {
  id: string;
  user_id: string;
  status: string;
  created_at: string;
  submitted_at: string | null;
  updated_at: string;
  form_data: Record<string, unknown> | null;
  invitation_id: string;
  form_invitations: {
    form_templates: {
      title: string | null;
    } | null;
  } | null;
}

interface LegacyShareRow {
  id: string;
  status: string;
  created_at: string;
  organization_id: string;
  submissions: {
    id: string;
    created_at: string;
    user_id: string;
    answers?: Record<string, unknown> | null;
    data?: Record<string, unknown> | null;
    forms?: {
      name?: string | null;
    } | null;
  } | null;
}

interface AuditEvent {
  id: string;
  event_type: string;
  event_data: Record<string, unknown>;
  created_at: string;
  user_id: string | null;
  entity_type: string;
  entity_id: string;
}

// Use centralized query keys for consistency
const submissionsQueryKey = (organizationId: string, filters = DEFAULT_SUBMISSION_FILTERS_KEY, page = DEFAULT_PAGE) =>
  [...orgQueryKeys.responsesList(organizationId, filters, page)] as const;

const legacySubmissionsQueryKey = (organizationId: string, filters = DEFAULT_SUBMISSION_FILTERS_KEY, page = DEFAULT_PAGE) =>
  [...orgQueryKeys.legacySubmissions(organizationId, filters, page)] as const;

const templatesQueryKey = (organizationId: string) =>
  [...orgQueryKeys.templates(organizationId)] as const;

const invitationsQueryKey = (organizationId: string, userId?: string | null) =>
  [...orgQueryKeys.invitations(organizationId, userId)] as const;

const renewalRequestsQueryKey = (organizationId: string) =>
  [...orgQueryKeys.renewalRequestsCount(organizationId)] as const;

const auditEventsQueryKey = (organizationId: string, page = DEFAULT_PAGE) =>
  [...orgQueryKeys.audit(organizationId, page)] as const;

async function fetchFormResponses(organizationId: string) {
  try {
    // Check session but don't block prefetch on transient failures
    const hasSession = await ensureAuthenticatedSession();
    if (!hasSession) {
      console.debug('[prefetch] No session for fetchFormResponses, skipping');
      return [];
    }

    const callRpc = () => supabase.rpc('get_org_form_responses', {
      p_org_id: organizationId,
      p_limit: DEFAULT_PAGE_SIZE,
      p_offset: 0
    });

    let { data, error } = await callRpc();

    if (error) {
      const msg = getErrorMessage(error);
      const code = getErrorCode(error);

      // Self-heal: if org membership record is missing, repair once and retry.
      if (code === 'P0001' && msg.includes('organization membership required')) {
        const { error: repairError } = await ensureMembershipForCurrentUser();
        if (!repairError) {
          const retry = await callRpc();
          data = retry.data;
          error = retry.error;
        }
      }
    }

    if (error) {
      console.warn('[prefetch] get_org_form_responses failed:', error);
      return [];
    }

    // Map RPC result to expected structure
    // Note: RPC returns template_title but it's not in generated types yet
    type RpcResponse = OrgFormResponse;
    return ((data || []) as unknown as RpcResponse[]).map(response => ({
      id: response.id,
      user_id: response.user_id,
      status: response.status,
      created_at: response.created_at,
      submitted_at: response.submitted_at,
      updated_at: response.updated_at,
      form_data: response.form_data,
      invitation_id: response.invitation_id,
      form_invitations: {
        form_templates: {
          title: response.template_title
        }
      },
      type: "form_response" as const,
    }));
  } catch (error) {
    logger.error('[prefetch] fetchFormResponses exception:', error);
    return [];
  }
}

async function fetchLegacySubmissions(organizationId: string) {
  const start = (DEFAULT_PAGE - 1) * DEFAULT_PAGE_SIZE;
  const end = start + DEFAULT_PAGE_SIZE - 1;

  const { data, error } = await supabase
    .from("shares")
    .select(
      `
        id,
        status,
        created_at,
        organization_id,
        submissions!inner(
          id,
          created_at,
          user_id,
          answers,
          data,
          forms(name)
        )
      `,
      { count: "exact" }
    )
    .eq("organization_id", organizationId)
    .order("created_at", { ascending: false })
    .range(start, end);

  if (error) throw error;

  return ((data || []) as unknown as LegacyShareRow[])
    .filter((share): share is LegacyShareRow & { submissions: NonNullable<LegacyShareRow["submissions"]> } => !!share.submissions)
    .map(share => {
      const submission = share.submissions;

      return {
        id: submission.id,
        user_id: submission.user_id,
        status: share.status,
        created_at: submission.created_at,
        submitted_at: submission.created_at,
        updated_at: share.created_at,
        form_data: (submission.data || submission.answers || {}) as Record<string, unknown>,
        form_invitations: {
          form_templates: {
            title: submission.forms?.name || "Legacy Submission",
          },
        },
        invitation_id: share.id,
        type: "legacy_submission" as const,
      };
    });
}

async function fetchAuditEvents(organizationId: string, page: number) {
  const start = (page - 1) * DEFAULT_PAGE_SIZE;
  const end = start + DEFAULT_PAGE_SIZE - 1;

  const { data, error, count } = await supabase
    .from("audit_events")
    .select("*", { count: "exact" })
    .eq("organization_id", organizationId)
    .order("created_at", { ascending: false })
    .range(start, end);

  if (error) throw error;

  return {
    events: (data || []) as AuditEvent[],
    totalCount: count || 0,
  };
}

async function fetchRenewalRequestsCount(organizationId: string): Promise<number> {
  try {
    const { count, error } = await supabase
      .from("invitation_renewal_requests")
      .select("id", { count: "exact", head: true })
      .eq("status", "pending")
      .eq("organization_id", organizationId);

    if (error) throw error;
    return count ?? 0;
  } catch (e) {
    logger.error("Failed to fetch renewal count:", e);
    return 0;
  }
}

async function safePrefetch<T>(
  queryClient: QueryClient, 
  key: readonly unknown[], 
  fn: () => Promise<T>,
  options?: { staleTime?: number; gcTime?: number }
) {
  // Skip if already cached and fresh
  const existingData = queryClient.getQueryData(key);
  const queryState = queryClient.getQueryState(key);
  
  if (existingData && queryState && !queryState.isInvalidated) {
    const dataAge = Date.now() - (queryState.dataUpdatedAt || 0);
    const staleTime = options?.staleTime ?? QUERY_STALE_TIME.DYNAMIC;
    
    // Don't refetch if data is still fresh
    if (dataAge < staleTime) {
      return;
    }
  }

  try {
    await queryClient.prefetchQuery({
      queryKey: key,
      queryFn: fn,
      staleTime: options?.staleTime ?? QUERY_STALE_TIME.DYNAMIC,
      gcTime: options?.gcTime ?? QUERY_GC_TIME.MEDIUM,
    });
  } catch (error) {
    logger.error("Failed to prefetch query", key, error);
  }
}

export async function prefetchOrgApplications(queryClient: QueryClient, organizationId: string) {
  await Promise.all([
    safePrefetch(queryClient, submissionsQueryKey(organizationId), () => fetchFormResponses(organizationId)),
    safePrefetch(queryClient, legacySubmissionsQueryKey(organizationId), () => fetchLegacySubmissions(organizationId)),
  ]);
}

export async function prefetchOrgTemplates(queryClient: QueryClient, organizationId: string) {
  await safePrefetch(
    queryClient, 
    templatesQueryKey(organizationId), 
    () => fetchFormTemplates(organizationId),
    { staleTime: QUERY_STALE_TIME.STATIC, gcTime: QUERY_GC_TIME.LONG }
  );
}

export async function prefetchOrgInvitations(queryClient: QueryClient, organizationId: string, userId?: string | null) {
  if (!userId) return;

  await safePrefetch(
    queryClient,
    invitationsQueryKey(organizationId, userId),
    () => fetchFormInvitations(organizationId, userId),
    { staleTime: QUERY_STALE_TIME.SEMI_STATIC, gcTime: QUERY_GC_TIME.MEDIUM }
  );
}

export async function prefetchOrgRenewalCount(queryClient: QueryClient, organizationId: string) {
  await safePrefetch(queryClient, renewalRequestsQueryKey(organizationId), () => fetchRenewalRequestsCount(organizationId));
}

export async function prefetchOrgAudit(queryClient: QueryClient, organizationId: string) {
  await safePrefetch(
    queryClient, 
    auditEventsQueryKey(organizationId), 
    () => fetchAuditEvents(organizationId, DEFAULT_PAGE),
    { staleTime: QUERY_STALE_TIME.STATIC, gcTime: QUERY_GC_TIME.LONG }
  );
}

/**
 * Prefetch all data needed for organization dashboard
 * Reduces waterfall loading by fetching critical data in parallel
 */
export async function prefetchOrgDashboardData(queryClient: QueryClient, organizationId: string, userId?: string | null) {
  await Promise.all([
    prefetchOrgApplications(queryClient, organizationId),
    prefetchOrgTemplates(queryClient, organizationId),
    prefetchOrgInvitations(queryClient, organizationId, userId),
    prefetchOrgRenewalCount(queryClient, organizationId),
  ]);
}

const normalizePath = (path: string) => path.split("?")[0];
const ORG_ROUTE_WHITELIST = /^\/org\/(dashboard|submissions|invitations|templates|audit|settings)(?:\/|$)/;

export function useOrgRoutePrefetch() {
  const queryClient = useQueryClient();
  const { organizationId, userId } = useOptimizedQueryKey();
  const { isReady, hasAccessToken } = useAuthReady();

  const prefetchRoute = useCallback(
    (path: string) => {
      // Skip prefetch if auth not ready or no valid token
      if (!organizationId || !isReady || !hasAccessToken) {
        return;
      }

      const normalized = normalizePath(path);

      if (!normalized.startsWith("/org/")) {
        return;
      }

      if (!ORG_ROUTE_WHITELIST.test(normalized)) {
        logIgnoredOrgRoutePrefetch(normalized);
        return;
      }
      const tasks: Promise<unknown>[] = [];

      if (normalized.startsWith("/org/dashboard")) {
        tasks.push(prefetchOrgDashboardData(queryClient, organizationId, userId));
      }

      if (normalized.startsWith("/org/submissions")) {
        tasks.push(prefetchOrgApplications(queryClient, organizationId));
      }

      if (normalized.startsWith("/org/invitations")) {
        tasks.push(prefetchOrgInvitations(queryClient, organizationId, userId));
        tasks.push(prefetchOrgRenewalCount(queryClient, organizationId));
        tasks.push(prefetchOrgTemplates(queryClient, organizationId));
      }

      if (normalized.startsWith("/org/templates")) {
        tasks.push(prefetchOrgTemplates(queryClient, organizationId));
      }

      if (normalized.startsWith("/org/audit")) {
        tasks.push(prefetchOrgAudit(queryClient, organizationId));
      }

      if (normalized.startsWith("/org/settings")) {
        tasks.push(prefetchOrgTemplates(queryClient, organizationId));
      }

      if (tasks.length === 0) {
        return;
      }

      void Promise.all(tasks).catch(error => {
        logger.error("Failed to prefetch organization route data", error);
      });
    },
    [organizationId, queryClient, userId, isReady, hasAccessToken]
  );

  return { prefetchRoute };
}

export { DEFAULT_SUBMISSION_FILTERS_KEY, RENEWAL_REQUESTS_QUERY_SCOPE };
